import { useState, useEffect, useRef } from "react";
import { Search, Calendar, Briefcase, Newspaper, Wrench, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";

type SearchResult = {
  id: string;
  title: string;
  type: "event" | "job" | "article" | "tool";
  url: string;
  description?: string;
  date?: string;
};

type GlobalSearchProps = {
  alwaysExpanded?: boolean;
};

const TOOLS = [
  { title: "Market Analysis", url: "/market", description: "Real-time BTC market data" },
  { title: "Portfolio Tracker", url: "/portfolio", description: "Track your Bitcoin holdings" },
  { title: "Price Alerts", url: "/tools", description: "Set custom price notifications" },
  { title: "Top Holders", url: "/top-holders", description: "Bitcoin whale tracker" },
  { title: "Sentiment Board", url: "/sentiment-board", description: "Community sentiment analysis" },
  { title: "Predict Market", url: "/predict-market", description: "Make market predictions" },
];

export const GlobalSearch = ({ alwaysExpanded = false }: GlobalSearchProps) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(alwaysExpanded);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const navigate = useNavigate();
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        if (!alwaysExpanded) {
          setIsExpanded(false);
        }
        setSelectedIndex(-1);
        setQuery("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [alwaysExpanded]);

  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded]);

  useEffect(() => {
    const searchData = async () => {
      if (query.trim().length < 2) {
        setResults([]);
        setIsOpen(false);
        return;
      }

      setIsLoading(true);
      setIsOpen(true);
      const searchQuery = query.trim().toLowerCase();
      const allResults: SearchResult[] = [];

      try {
        // Search events
        const { data: events } = await supabase
          .from("bitcoin_events")
          .select("id, event_name, event_date, slug, location")
          .or(`event_name.ilike.%${searchQuery}%,location.ilike.%${searchQuery}%`)
          .eq("is_active", true)
          .limit(5);

        if (events) {
          allResults.push(
            ...events.map((event) => ({
              id: event.id,
              title: event.event_name,
              type: "event" as const,
              url: event.slug ? `/events/${event.slug}` : `/events`,
              description: event.location,
              date: event.event_date,
            }))
          );
        }

        // Search jobs
        const { data: jobs } = await supabase
          .from("job_postings")
          .select("id, job_title, company_name, location")
          .or(`job_title.ilike.%${searchQuery}%,company_name.ilike.%${searchQuery}%`)
          .eq("is_active", true)
          .limit(5);

        if (jobs) {
          allResults.push(
            ...jobs.map((job) => ({
              id: job.id,
              title: job.job_title,
              type: "job" as const,
              url: `/jobs/${job.id}`,
              description: `${job.company_name} • ${job.location}`,
            }))
          );
        }

        // Search articles
        const { data: articles } = await supabase
          .from("news_articles")
          .select("id, headline, published_at, source")
          .ilike("headline", `%${searchQuery}%`)
          .order("published_at", { ascending: false })
          .limit(5);

        if (articles) {
          allResults.push(
            ...articles.map((article) => ({
              id: article.id,
              title: article.headline,
              type: "article" as const,
              url: `/news/${article.id}`,
              description: article.source,
              date: article.published_at,
            }))
          );
        }

        // Search tools
        const toolResults = TOOLS.filter(
          (tool) =>
            tool.title.toLowerCase().includes(searchQuery) ||
            tool.description.toLowerCase().includes(searchQuery)
        ).map((tool) => ({
          id: tool.url,
          title: tool.title,
          type: "tool" as const,
          url: tool.url,
          description: tool.description,
        }));

        allResults.push(...toolResults);
        setResults(allResults);
      } catch (error) {
        console.error("Search error:", error);
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchData, 300);
    return () => clearTimeout(debounceTimer);
  }, [query]);

  const handleResultClick = (url: string) => {
    navigate(url);
    setQuery("");
    setResults([]);
    setIsOpen(false);
    setIsExpanded(false);
    setSelectedIndex(-1);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || results.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && results[selectedIndex]) {
          handleResultClick(results[selectedIndex].url);
        }
        break;
      case "Escape":
        setIsOpen(false);
        if (!alwaysExpanded) {
          setIsExpanded(false);
        }
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  const getIcon = (type: SearchResult["type"]) => {
    switch (type) {
      case "event":
        return <Calendar className="h-4 w-4" />;
      case "job":
        return <Briefcase className="h-4 w-4" />;
      case "article":
        return <Newspaper className="h-4 w-4" />;
      case "tool":
        return <Wrench className="h-4 w-4" />;
    }
  };

  const getTypeLabel = (type: SearchResult["type"]) => {
    switch (type) {
      case "event":
        return "Event";
      case "job":
        return "Job";
      case "article":
        return "Article";
      case "tool":
        return "Tool";
    }
  };

  const clearSearch = () => {
    setQuery("");
    setResults([]);
    setIsOpen(false);
    setSelectedIndex(-1);
  };

  const toggleSearch = () => {
    if (alwaysExpanded) return;
    setIsExpanded(!isExpanded);
    if (isExpanded) {
      setQuery("");
      setResults([]);
      setIsOpen(false);
      setSelectedIndex(-1);
    }
  };

  if (alwaysExpanded) {
    return (
      <div ref={searchRef} className="relative w-full">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            ref={inputRef}
            type="text"
            placeholder="Search events, jobs, articles, tools..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => query.length >= 2 && setIsOpen(true)}
            className="pl-10 h-10 bg-background border-border"
          />
        </div>

        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-popover border border-border rounded-lg shadow-lg overflow-hidden z-50 max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                Searching...
              </div>
            ) : results.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                No results found for "{query}"
              </div>
            ) : (
              <div className="py-2">
                {results.map((result, index) => (
                  <button
                    key={`${result.type}-${result.id}`}
                    onClick={() => handleResultClick(result.url)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    className={cn(
                      "w-full px-4 py-3 flex items-start gap-3 text-left hover:bg-muted/50 transition-colors",
                      selectedIndex === index && "bg-muted/50"
                    )}
                  >
                    <div className="mt-0.5 text-muted-foreground">{getIcon(result.type)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-foreground truncate">
                          {result.title}
                        </p>
                        <span className="text-xs px-1.5 py-0.5 rounded bg-primary/10 text-primary shrink-0">
                          {getTypeLabel(result.type)}
                        </span>
                      </div>
                      {result.description && (
                        <p className="text-xs text-muted-foreground truncate">
                          {result.description}
                        </p>
                      )}
                      {result.date && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(result.date).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div ref={searchRef} className="relative">
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleSearch}
        className="relative"
        aria-label="Open search"
      >
        <Search className="h-5 w-5" />
      </Button>
      
      {isExpanded && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm" onClick={toggleSearch}>
          <div className="fixed top-20 left-1/2 -translate-x-1/2 w-full max-w-2xl px-4" onClick={(e) => e.stopPropagation()}>
            <div className="relative animate-in fade-in zoom-in-95 duration-200">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
              <Input
                ref={inputRef}
                type="text"
                placeholder="Search events, jobs, articles, tools..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => query.length >= 2 && setIsOpen(true)}
                className="pl-10 pr-10 h-12 bg-popover border-border shadow-2xl text-base"
              />
              <button
                onClick={toggleSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors z-10"
                aria-label="Close search"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {isOpen && (
              <div className="mt-2 bg-popover border border-border rounded-lg shadow-lg overflow-hidden max-h-96 overflow-y-auto animate-in fade-in slide-in-from-top-2 duration-200">
                {isLoading ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    Searching...
                  </div>
                ) : results.length === 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    No results found for "{query}"
                  </div>
                ) : (
                  <div className="py-2">
                    {results.map((result, index) => (
                      <button
                        key={`${result.type}-${result.id}`}
                        onClick={() => handleResultClick(result.url)}
                        onMouseEnter={() => setSelectedIndex(index)}
                        className={cn(
                          "w-full px-4 py-3 flex items-start gap-3 text-left hover:bg-muted/50 transition-colors",
                          selectedIndex === index && "bg-muted/50"
                        )}
                      >
                        <div className="mt-0.5 text-muted-foreground">{getIcon(result.type)}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-sm font-medium text-foreground truncate">
                              {result.title}
                            </p>
                            <span className="text-xs px-1.5 py-0.5 rounded bg-primary/10 text-primary shrink-0">
                              {getTypeLabel(result.type)}
                            </span>
                          </div>
                          {result.description && (
                            <p className="text-xs text-muted-foreground truncate">
                              {result.description}
                            </p>
                          )}
                          {result.date && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {new Date(result.date).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
